import { useMemo, useState, useEffect } from "react"
import { collection, query, where, getCountFromServer, Timestamp } from "firebase/firestore"
import { db } from "@/config/firebase"
import { useAuth } from "@/features/auth/context/AuthContext"
import { usePlan } from "@/shared/hooks/usePlan"

export interface ProfileData {
  displayName: string
  email: string
  photoURL: string | null
  memberSince: Date | null
  creditsRemaining: number
  creditsUsed: number
  creditsPurchased: number
  productsThisMonth: number
  isLoading: boolean
}

const useProfileData = (): ProfileData => {
  const { user, userDetails, loading: authLoading } = useAuth()
  const { creditsRemaining, creditsUsed, creditsPurchased, isLoading: planLoading } = usePlan()

  const [productsThisMonth, setProductsThisMonth] = useState(0)
  const [countLoading, setCountLoading] = useState(true)

  useEffect(() => {
    if (!user?.uid) {
      setProductsThisMonth(0)
      setCountLoading(false)
      return
    }

    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const startTimestamp = Timestamp.fromDate(startOfMonth)

    const q = query(collection(db, "users", user.uid, "products"), where("createdAt", ">=", startTimestamp))

    getCountFromServer(q)
      .then(snap => setProductsThisMonth(snap.data().count))
      .catch(() => setProductsThisMonth(0))
      .finally(() => setCountLoading(false))
  }, [user?.uid])

  return useMemo<ProfileData>(() => {
    let memberSince: Date | null = null
    if (userDetails?.createdAt?.toDate) {
      memberSince = userDetails.createdAt.toDate()
    } else if (user?.metadata?.creationTime) {
      memberSince = new Date(user.metadata.creationTime)
    }

    return {
      displayName: user?.displayName ?? userDetails?.full_name ?? "User",
      email: user?.email ?? userDetails?.email ?? "",
      photoURL: user?.photoURL ?? null,
      memberSince,
      creditsRemaining,
      creditsUsed,
      creditsPurchased,
      productsThisMonth,
      isLoading: authLoading || planLoading || countLoading
    }
  }, [
    user,
    userDetails,
    creditsRemaining,
    creditsUsed,
    creditsPurchased,
    authLoading,
    planLoading,
    productsThisMonth,
    countLoading
  ])
}

export { useProfileData }
